import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { analytics } from "~/analytics";
import { STORAGE_KEYS } from "~/lib/constants";
import { StreakClientService } from "~/services/streak.client";
import { ensureUserData } from "~/stores/reactQuery/useUserQuery";
import type { User } from "~/types/user";

dayjs.extend(utc);
dayjs.extend(timezone);

type ActivationStorage = Pick<Storage, "getItem" | "setItem">;

export type FirstWeekCompletionInput = {
  userId: number;
  createdAtUtc: string;
  timezone: string;
  completionDates: Array<string>;
  includeCurrentCompletionDay?: boolean;
  now?: Date;
  storage?: ActivationStorage | null;
};

type CheckFirstWeekThreeDayCompletionInput = {
  user?: Pick<User, "id" | "createdAtUtc" | "timezone"> | null;
  includeCurrentCompletionDay?: boolean;
};

function getFirstWeekWindow(createdAtUtc: string, userTimezone: string, now: Date) {
  try {
    const createdAt = dayjs(createdAtUtc);
    const observedAt = dayjs(now);
    if (!createdAt.isValid() || !observedAt.isValid() || !userTimezone) return null;

    const signupDay = createdAt.tz(userTimezone).startOf("day");
    const today = observedAt.tz(userTimezone).startOf("day");
    const lastEligibleDay = signupDay.add(6, "day");
    if (today.isBefore(signupDay) || today.isAfter(lastEligibleDay)) return null;

    return {
      firstDate: signupDay.format("YYYY-MM-DD"),
      lastDate: lastEligibleDay.format("YYYY-MM-DD"),
      todayDate: today.format("YYYY-MM-DD"),
    };
  } catch {
    return null;
  }
}

export function isInFirstWeekCalendarWindow(createdAtUtc: string, userTimezone: string, now = new Date()): boolean {
  return getFirstWeekWindow(createdAtUtc, userTimezone, now) !== null;
}

function getBrowserStorage(): ActivationStorage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function markFirstWeekThreeDayCompletion({
  userId,
  createdAtUtc,
  timezone: userTimezone,
  completionDates,
  includeCurrentCompletionDay = false,
  now = new Date(),
  storage = getBrowserStorage(),
}: FirstWeekCompletionInput): boolean {
  if (!storage || !Number.isFinite(userId) || !userTimezone || !Array.isArray(completionDates)) return false;

  try {
    const window = getFirstWeekWindow(createdAtUtc, userTimezone, now);
    if (!window) return false;

    const storageKey = `${STORAGE_KEYS.FIRST_WEEK_COMPLETION_ACTIVATION}:user:${userId}`;
    if (storage.getItem(storageKey) === "true") return false;

    const eligibleDates = new Set(
      completionDates.filter(
        (date) =>
          typeof date === "string" &&
          /^\d{4}-\d{2}-\d{2}$/.test(date) &&
          dayjs(date).isValid() &&
          dayjs(date).format("YYYY-MM-DD") === date &&
          date >= window.firstDate &&
          date <= window.lastDate,
      ),
    );

    if (includeCurrentCompletionDay) eligibleDates.add(window.todayDate);
    if (eligibleDates.size < 3) return false;

    storage.setItem(storageKey, "true");
    return true;
  } catch {
    return false;
  }
}

export async function checkFirstWeekThreeDayCompletion({
  user: providedUser,
  includeCurrentCompletionDay = false,
}: CheckFirstWeekThreeDayCompletionInput = {}): Promise<void> {
  try {
    const user = providedUser ?? (await ensureUserData());
    if (!user || !isInFirstWeekCalendarWindow(user.createdAtUtc, user.timezone)) return;

    const history = await StreakClientService.getStreakHistory();
    if (!history) return;

    const reachedThreshold = markFirstWeekThreeDayCompletion({
      userId: user.id,
      createdAtUtc: user.createdAtUtc,
      timezone: user.timezone,
      completionDates: history.dates,
      includeCurrentCompletionDay,
    });
    if (!reachedThreshold) return;

    analytics.track({
      event: "New User Activated",
      properties: { has_completed_3_days_in_first_7: true },
    });
  } catch {
    // Eligibility and analytics failures must never interrupt playback.
  }
}
